import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Heart, Flower, Pen, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ContextualNotificationPrompt } from "@/components/notifications/ContextualNotificationPrompt";

interface GratitudeExercisesProps {
  exerciseType: 'rose-bud-thorn' | 'gratitude-letter' | 'meditation';
  onComplete: (data: any) => void;
  className?: string;
}

export default function GratitudeExercises({ 
  exerciseType, 
  onComplete,
  className = '' 
}: GratitudeExercisesProps) {
  const [rose, setRose] = useState('');
  const [bud, setBud] = useState('');
  const [thorn, setThorn] = useState('');
  const [letterRecipient, setLetterRecipient] = useState('');
  const [letterContent, setLetterContent] = useState('');
  const [meditationNotes, setMeditationNotes] = useState('');

  const handleSubmit = () => {
    switch (exerciseType) {
      case 'rose-bud-thorn':
        onComplete({ rose, bud, thorn });
        break;
      case 'gratitude-letter':
        onComplete({ recipient: letterRecipient, content: letterContent });
        break;
      case 'meditation':
        onComplete({ notes: meditationNotes });
        break;
    }
  };

  const renderExercise = () => {
    switch (exerciseType) {
      case 'rose-bud-thorn':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rose" className="flex items-center gap-2">
                <span className="font-semibold text-secondary">Rose</span>
                <span>- Something that went well today</span>
              </Label>
              <Textarea
                id="rose"
                value={rose}
                onChange={(e) => setRose(e.target.value)}
                placeholder="What made you smile or feel grateful today?"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bud" className="flex items-center gap-2">
                <span className="font-semibold text-primary">Bud</span>
                <span>- Something you're looking forward to</span>
              </Label>
              <Textarea
                id="bud"
                value={bud}
                onChange={(e) => setBud(e.target.value)}
                placeholder="What possibilities excite you?"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="thorn" className="flex items-center gap-2">
                <span className="font-semibold text-muted-foreground">Thorn</span>
                <span>- A challenge you faced</span>
              </Label>
              <Textarea
                id="thorn"
                value={thorn}
                onChange={(e) => setThorn(e.target.value)}
                placeholder="What was difficult? What can you learn from it?"
                rows={3}
              />
            </div>
          </div>
        );

      case 'gratitude-letter':
        return (
          <div className="space-y-4">
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Pen className="w-4 h-4" />
                Write a Gratitude Letter
              </h4>
              <p className="text-sm text-muted-foreground">
                Research shows that writing (not sending) a letter of gratitude to someone who has positively 
                impacted your life can significantly boost happiness and reduce depression for up to 3 months.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="recipient">Who are you grateful for?</Label>
              <Textarea
                id="recipient"
                value={letterRecipient}
                onChange={(e) => setLetterRecipient(e.target.value)}
                placeholder="Name and how they've impacted you..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="letter">Your Letter</Label>
              <Textarea
                id="letter"
                value={letterContent}
                onChange={(e) => setLetterContent(e.target.value)}
                placeholder="Dear [Name], I want to express my gratitude for..."
                rows={8}
              />
            </div>

            <div className="text-xs text-muted-foreground">
              💡 Tip: Be specific about what they did and how it affected you. You don't have to send this - the act of writing is powerful on its own.
            </div>
          </div>
        );

      case 'meditation':
        return (
          <div className="space-y-4">
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Gratitude Meditation
              </h4>
              <p className="text-sm text-muted-foreground mb-3">
                Take 5 minutes for a guided reflection on gratitude.
              </p>
              <div className="space-y-2 text-sm">
                <p>1. Find a comfortable position and close your eyes</p>
                <p>2. Take 3 deep breaths, releasing tension with each exhale</p>
                <p>3. Bring to mind someone you're grateful for</p>
                <p>4. Notice the warmth of appreciation in your body</p>
                <p>5. Expand to appreciate simple things: your breath, shelter, food</p>
                <p>6. Send gratitude to yourself for practicing self-care</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="meditation-notes">Reflection Notes (Optional)</Label>
              <Textarea
                id="meditation-notes"
                value={meditationNotes}
                onChange={(e) => setMeditationNotes(e.target.value)}
                placeholder="What came up for you during the meditation?"
                rows={4}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card className={`p-6 space-y-4 ${className}`}>
      {renderExercise()}
      <Button onClick={handleSubmit} className="w-full">
        Complete Exercise
      </Button>
      <ContextualNotificationPrompt context="gratitude" />
    </Card>
  );
}
